import { useEffect, useRef } from 'react'

import {
  PREFETCH_RETRY_ATTEMPTS,
  cancelHomePrefetchQueries,
  collectFailedPrefetchRequests,
  enrichHomePrefetchQueryOptions,
  isFetchQueryOptions,
  PrefetchableQueryOptions,
} from 'common/query-policy'
import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { getFaqAnswerQueryOptions } from 'pages/frequent-questions/hooks/queries/getFaqAnswerQueryOptions'
import {
  getFaqsQueryKey,
  GetFAQsResponse,
} from 'pages/frequent-questions/hooks/queries/getFaqsQueryOptions'

import { FetchQueryOptions, QueryClient, useQueryClient } from '@tanstack/react-query'

import { buildFaqAnswerPrefetchRequestDescriptor } from '../utils/buildHomePrefetchRequestDescriptor'
import { collectHomePrefetchQueryOptions } from '../utils/collectHomePrefetchQueryOptions'
import { deriveHomeFeatureFlags } from '../utils/deriveHomeFeatureFlags'
import { isHomePrefetchReady } from '../utils/isHomePrefetchReady'
import {
  sendHomePrefetchEventData,
  sendHomePrefetchOrchestrationError,
} from '../utils/sendHomePrefetchEventData'

const FAQ_ANSWERS_PREFETCH_LIMIT = 3

const prefetchValidatedQuery = async (queryClient: QueryClient, queryOptions: unknown) => {
  if (!isFetchQueryOptions(queryOptions)) return

  await queryClient.prefetchQuery({
    ...(queryOptions as FetchQueryOptions),
    retry: PREFETCH_RETRY_ATTEMPTS,
  })
}

const prefetchFaqAnswers = async (
  queryClient: QueryClient,
  language: Parameters<typeof getFaqAnswerQueryOptions>[0]['language'],
  userSelectedEntity: Parameters<typeof getFaqAnswerQueryOptions>[0]['userSelectedEntity'],
) => {
  const faqs = queryClient.getQueryData<GetFAQsResponse>(
    getFaqsQueryKey({ queryText: '', language, userSelectedEntity }),
  )

  if (!faqs) return []

  const faqAnswerQueryOptions = faqs.hits.hit
    .slice(0, FAQ_ANSWERS_PREFETCH_LIMIT)
    .map(({ id }) =>
      enrichHomePrefetchQueryOptions(
        getFaqAnswerQueryOptions({ id, language, userSelectedEntity }),
        buildFaqAnswerPrefetchRequestDescriptor(id, language, userSelectedEntity),
      ),
    )

  await Promise.allSettled(
    faqAnswerQueryOptions.map((queryOption) => prefetchValidatedQuery(queryClient, queryOption)),
  )

  return faqAnswerQueryOptions
}

const useHomePrefetch = () => {
  const {
    privacyCenterContent,
    userCountry,
    userSelectedEntity,
    userAccountType,
    userLanguage,
    userEntities,
    isUSBranch,
    isAppLoading,
  } = useAppContext()

  const queryClient = useQueryClient()
  const lastPrefetchKeyRef = useRef<string | null>(null)

  useEffect(() => {
    let isCancelled = false

    const session = {
      privacyCenterContent,
      userCountry,
      userSelectedEntity,
      userAccountType,
      userLanguage,
    }

    if (!isHomePrefetchReady(isAppLoading, session)) return undefined

    const {
      privacyCenterContent: readyPrivacyCenterContent,
      userCountry: readyUserCountry,
      userSelectedEntity: readyUserSelectedEntity,
      userAccountType: readyUserAccountType,
      userLanguage: readyUserLanguage,
    } = session

    const prefetchKey = `${readyUserSelectedEntity}-${readyUserCountry}-${readyUserAccountType}-${readyUserLanguage}`

    if (lastPrefetchKeyRef.current === prefetchKey) return undefined

    const homeContent = readyPrivacyCenterContent.components.home?.content

    if (!homeContent) return undefined

    lastPrefetchKeyRef.current = prefetchKey

    const featureFlags = deriveHomeFeatureFlags(homeContent)
    const queryOptionsList = collectHomePrefetchQueryOptions({
      privacyCenterContent: readyPrivacyCenterContent,
      userCountry: readyUserCountry,
      userSelectedEntity: readyUserSelectedEntity,
      userAccountType: readyUserAccountType,
      userLanguage: readyUserLanguage,
      userEntities,
      isUSBranch,
      featureFlags,
    })

    if (queryOptionsList.length === 0) return undefined

    const runPrefetch = async () => {
      const prefetchedQueryOptions: PrefetchableQueryOptions[] = [...queryOptionsList]

      await Promise.allSettled(
        queryOptionsList.map((queryOption) => prefetchValidatedQuery(queryClient, queryOption)),
      )

      if (isCancelled) return

      if (featureFlags.shouldPrefetchFaq) {
        const faqAnswerQueryOptions = await prefetchFaqAnswers(
          queryClient,
          readyUserLanguage,
          readyUserSelectedEntity,
        )

        prefetchedQueryOptions.push(...faqAnswerQueryOptions)
      }

      if (isCancelled) return

      const failedRequests = collectFailedPrefetchRequests(queryClient, prefetchedQueryOptions)

      if (failedRequests.length > 0) {
        await sendHomePrefetchEventData({
          prefetchKey,
          failedRequests,
        })
      }
    }

    runPrefetch().catch(async (error: unknown) => {
      const failedRequests = collectFailedPrefetchRequests(queryClient, queryOptionsList)

      await sendHomePrefetchOrchestrationError(prefetchKey, error, failedRequests)
    })

    return () => {
      isCancelled = true
      cancelHomePrefetchQueries(queryClient)
    }
  }, [
    isAppLoading,
    isUSBranch,
    privacyCenterContent,
    queryClient,
    userAccountType,
    userCountry,
    userEntities,
    userLanguage,
    userSelectedEntity,
  ])
}

export { useHomePrefetch }
